import React from "react";
import { Banner, Button, Link, LoadingIndicator, Tag, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import type { IconType } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { usePackageActivity } from "#hooks/usePackageActivities";
import {
  activityTypeLabel,
  formatActivityDateTime,
  reviewNoteDocTypeLabel,
  sourceTypeLabel,
} from "../../activityLabels";
import * as styles from "../../styles_vanilla.css";
import type { ActivityReviewNote, ActivityRow } from "#hooks/usePackageActivities";

export type ActivityCardProps = {
  activity: ActivityRow;
  expanded: boolean;
  onToggle: () => void;
  /** Arena the detail read is scoped to; undefined until the host app resolves
   *  it, which holds the read back. */
  arenaId: number | undefined;
};

type BadgeTone = "success" | "danger" | "neutral";

const TONE_CLASS: Record<BadgeTone, string> = {
  success: styles.badgeSuccess,
  danger: styles.badgeDanger,
  neutral: styles.badgeNeutral,
};

const TONE_COLOR: Record<BadgeTone, Colors> = {
  success: Colors.Success500,
  danger: Colors.Danger500,
  neutral: Colors.Neutral500,
};

/**
 * The verdict for one review note. `outcome` is a free-form string on the wire,
 * and a null one means the note has never been evaluated — it must read as
 * pending, never as a pass, so anything unrecognized falls through to
 * "Not evaluated".
 */
function reviewNoteBadge(outcome: string | null): {
  text: string;
  icon: IconType;
  tone: BadgeTone;
} {
  switch (outcome) {
    case "PASS":
      return { text: "Passed", icon: "check", tone: "success" };
    case "FAIL":
      return { text: "Failed", icon: "exclamation-triangle", tone: "danger" };
    case "SKIPPED":
      return { text: "Skipped", icon: "minus-circle", tone: "neutral" };
    default:
      return { text: "Not evaluated", icon: "clock", tone: "neutral" };
  }
}

/** A review note: its verdict, the document type it was evaluated against, when
 *  it was evaluated, and the note itself. */
const ReviewNote: React.FC<{ note: ActivityReviewNote }> = ({ note }) => {
  const badge = reviewNoteBadge(note.outcome);

  return (
    <li className={styles.reviewNote}>
      <div className={styles.reviewNoteVerdict}>
        <span className={`${styles.badge} ${TONE_CLASS[badge.tone]}`}>
          <Icon type={badge.icon} color={TONE_COLOR[badge.tone]} size="small" />
          {badge.text}
        </span>
        <Text variant="secondary-sm" asSpan>
          {reviewNoteDocTypeLabel(note.docType)}
        </Text>
        {note.evaluatedAt && (
          <Text variant="secondary-sm" asSpan>
            {`Evaluated ${formatActivityDateTime(note.evaluatedAt)}`}
          </Text>
        )}
      </div>
      <Text variant="regular-md" asSpan>
        {note.content}
      </Text>
    </li>
  );
};

/**
 * One activity in the Activity tab: the always-visible summary card (name,
 * type, AI summary) plus the expanded detail below it. The detail read only
 * fires once the card is opened, so a package with many activities costs one
 * list query rather than one query per row.
 */
export const ActivityCard: React.FC<ActivityCardProps> = ({
  activity,
  expanded,
  onToggle,
  arenaId,
}) => {
  const {
    activity: detail,
    loading,
    error,
    notFound,
  } = usePackageActivity(expanded ? activity.id : null, arenaId);

  // The wire order of review notes isn't guaranteed; `ordinal` is the order the
  // reviewer should read them in.
  const reviewNotes = detail
    ? [...detail.reviewNotes].sort((a, b) => a.ordinal - b.ordinal)
    : [];

  return (
    <div className={styles.card}>
      <Text variant="strong-lg" asSpan>
        {activity.name}
      </Text>

      <div className={styles.metaRow}>
        <Tag variant="neutral" label={activityTypeLabel(activity.activityType)} />
      </div>

      {activity.summary && (
        <div className={styles.block}>
          <Text variant="secondary-sm" asSpan>
            Summary
          </Text>
          <Text variant="regular-md">{activity.summary}</Text>
        </div>
      )}

      <div className={styles.toggleRow}>
        <Button displayAsLink isInline onClick={onToggle}>
          {expanded ? "Hide details" : "Show details"}
        </Button>
      </div>

      {expanded && (
        <div className={styles.details}>
          {error && (
            <Banner
              variant="danger"
              title="Couldn’t load activity details"
              description={error.message}
            />
          )}

          {loading && !detail && <LoadingIndicator size="small" />}

          {notFound && (
            <Text variant="secondary-md" asSpan>
              This activity is no longer available.
            </Text>
          )}

          {reviewNotes.length > 0 && (
            <div className={styles.reviewNotesBox}>
              <Text variant="strong-md" asSpan>
                Review notes
              </Text>
              <ul className={styles.reviewNoteList}>
                {reviewNotes.map((note) => (
                  <ReviewNote key={note.id} note={note} />
                ))}
              </ul>
            </div>
          )}

          {detail && detail.sources.length > 0 && (
            <div className={styles.sourceBox}>
              <Text variant="strong-md" asSpan>
                Sources
              </Text>
              <ul className={styles.sourceList}>
                {/* An issue key can repeat across an activity's sources, so the
                    row index keeps otherwise-identical rows distinct. */}
                {detail.sources.map((source, index) => (
                  <li
                    key={`${source.sourceType}:${source.source}:${index}`}
                    className={styles.sourceRow}
                  >
                    <Text variant="strong-md" asSpan>
                      Reference:
                    </Text>
                    <Link href={source.url} isInline shouldOpenInNewTab>
                      {source.source}
                    </Link>
                    <Text variant="strong-md" asSpan>
                      Type:
                    </Text>
                    <Text variant="regular-md" asSpan>
                      {sourceTypeLabel(source.sourceType)}
                    </Text>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
